import { ownedActionConfig } from '../lib/accountBoundary';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getWebProfile,
  getWebEmailPreferences,
  getWebPeopleDiscoverySettings,
  getWebDataExportStatus,
  createWebDataExportDownload,
  requestWebDataExport,
  resendWebEmailVerification,
  updateWebEmailPreferences,
  updateWebPeopleDiscoverySettings,
  updateWebProfile,
  updateWebProfileVisibility,
  updateWebProfileName,
  type UpdateWebProfilePayload,
  type WebEmailPreferences,
  type UpdateWebPeopleDiscoverySettingsPayload,
  type WebDataExportResponse,
} from '../lib/profile';
import { authKeys } from './useAuth';

export const profileKeys = {
  all: ['profile'] as const,
  current: () => [...profileKeys.all, 'current'] as const,
  emailPreferences: () => [...profileKeys.all, 'email-preferences'] as const,
  peopleDiscovery: () => [...profileKeys.all, 'people-discovery'] as const,
  dataExport: () => [...profileKeys.all, 'data-export'] as const,
};

export function useWebProfile(enabled = true) {
  return useQuery({
    queryKey: profileKeys.current(),
    queryFn: getWebProfile,
    staleTime: 2 * 60 * 1000,
    retry: false,
    enabled,
  });
}

export function useUpdateWebProfile() {
  const owner = ownedActionConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateWebProfilePayload) => updateWebProfile(payload, owner),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: profileKeys.current() }),
        queryClient.invalidateQueries({ queryKey: authKeys.user() }),
      ]);
    },
  });
}

export function useUpdateWebProfileName() {
  const owner = ownedActionConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => updateWebProfileName(name, owner),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: profileKeys.current() }),
        queryClient.invalidateQueries({ queryKey: authKeys.user() }),
      ]);
    },
  });
}

export function useResendEmailVerification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: resendWebEmailVerification,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: authKeys.user() }),
        queryClient.invalidateQueries({ queryKey: profileKeys.current() }),
      ]);
    },
  });
}

export function useUpdateWebProfileVisibility() {
  const owner = ownedActionConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (visibility: string) => updateWebProfileVisibility(visibility, owner),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: profileKeys.current() });
    },
  });
}

export function useRequestWebDataExport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: requestWebDataExport,
    onSuccess: (data) => {
      queryClient.setQueryData(profileKeys.dataExport(), data);
    },
  });
}

export function useWebDataExportStatus(enabled = true) {
  return useQuery({
    queryKey: profileKeys.dataExport(),
    queryFn: getWebDataExportStatus,
    staleTime: 10_000,
    retry: false,
    enabled,
    refetchInterval: (query) => {
      const status = (query.state.data as WebDataExportResponse | undefined)?.status;
      return status === 'PENDING' || status === 'PROCESSING' ? 10_000 : false;
    },
  });
}

export function useCreateWebDataExportDownload() {
  return useMutation({
    mutationFn: createWebDataExportDownload,
  });
}

export function useWebEmailPreferences(enabled = true) {
  return useQuery({
    queryKey: profileKeys.emailPreferences(),
    queryFn: getWebEmailPreferences,
    staleTime: 2 * 60 * 1000,
    retry: false,
    enabled,
  });
}

export function useUpdateWebEmailPreferences() {
  const owner = ownedActionConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (preferences: WebEmailPreferences) => updateWebEmailPreferences(preferences, owner),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: profileKeys.emailPreferences() });
    },
  });
}

export function useWebPeopleDiscoverySettings(enabled = true) {
  return useQuery({
    queryKey: profileKeys.peopleDiscovery(),
    queryFn: getWebPeopleDiscoverySettings,
    staleTime: 2 * 60 * 1000,
    retry: false,
    enabled,
  });
}

export function useUpdateWebPeopleDiscoverySettings() {
  const owner = ownedActionConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: UpdateWebPeopleDiscoverySettingsPayload) =>
      updateWebPeopleDiscoverySettings(settings, owner),
    onSuccess: (data) => {
      queryClient.setQueryData(profileKeys.peopleDiscovery(), data);
    },
  });
}
